import React, { useState, useEffect } from "react";
import { MarketTicker_Legacy } from "@/types/models";
import { mockTickerService } from "@/lib/mockTickerService";
import { formatPercent } from "@/lib/numberFormat";
import {
  formatTickerAmountByCurrency,
  formatTickerPriceLabel,
  toDisplayDirectionalChange,
} from "@/lib/tickerDirection";
import TrendPyramidIcon from "@/components/TrendPyramidIcon";

const toFiniteNumber = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const LiveMarketTicker = () => {
  const [tickers, setTickers] = useState<MarketTicker_Legacy[]>([]);

  // Load data from database on component mount
  useEffect(() => {
    const loadTickerData = async () => {
      try {
        console.log("LiveMarketTicker: Loading ticker data from database...");
        const data = await mockTickerService.getAll();
        setTickers(data);
        console.log("LiveMarketTicker: Successfully loaded ticker data from database");
      } catch (error) {
        console.error("LiveMarketTicker: Failed to load ticker data:", error);
        // Fallback to empty array if database fails
        setTickers([]);
      }
    };

    loadTickerData();
  }, []); // Empty dependency array means this runs only once on mount

  const repeatedTickers = tickers.length ? [...tickers, ...tickers] : [];

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "#000",
        borderBottom: "1px solid #222",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <div className="flex overflow-hidden" style={{ height: "30px" }}>
        <div className="flex animate-market-marquee">
          {repeatedTickers.map((item, index) => {
            const price = toFiniteNumber(item.price);
            const tickerIdentity = { symbol: item.symbol, name: item.name, currency: item.currency };
            const { change, changePercent } = toDisplayDirectionalChange(
              tickerIdentity,
              item.change,
              item.changePercent
            );
            const changeColor = change >= 0 ? "text-green-400" : "text-red-400";
            const changeDirection = change >= 0 ? "up" : "down";
            const formattedChange = formatTickerAmountByCurrency(tickerIdentity, Math.abs(change));

            return (
              <div key={`${item.id}-${index}`} className="flex items-center justify-center gap-3 px-4 h-6 whitespace-nowrap">
                <span className="text-white font-normal text-xs">{item.name}</span>
                <span className="text-yellow-400 font-bold text-xs">
                  {formatTickerPriceLabel(tickerIdentity, price)}
                </span>
                <span className={`${changeColor} font-medium text-xs inline-flex items-center gap-1`}>
                  <TrendPyramidIcon direction={changeDirection} className={changeColor} />
                  {formattedChange} ({formatPercent(changePercent)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes market-marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-market-marquee {
              display: flex;
              animation: market-marquee 30s linear infinite;
            }
            .animate-market-marquee:hover {
              animation-play-state: paused;
            }
          `,
        }}
      />
    </div>
  );
};

export default LiveMarketTicker;
